import * as React from 'react';
import { useDropzone } from 'react-dropzone';
import { isEqual } from 'vet-bones/utils';

import { FileExtension } from 'src/constants/fileExtensions';
import { useCreateProjectState } from 'src/containers/CreateProjectOverlay/hooks/useCreateProjectState';
import { defaultProjectFileUploadOptions } from 'src/store/state/projects';
import * as copy from 'src/templates/copy';
import { FileCreationMetadata } from 'src/types/fileUpload';
import { mergeEditedFieldLabels } from 'src/utils/csv/mergeEditedFieldLabels';
import { usePrevious } from 'src/utils/hooks/usePrevious';
import { useProjectMultipleFileUploadsParser } from 'src/utils/hooks/useProjectFileUploadParser';

type UseFileUploadStateArgs = {
  setIsAddDataDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setDrawerFullSize: React.Dispatch<React.SetStateAction<boolean>>;
  setDrawerTitle: React.Dispatch<React.SetStateAction<string>>;
};

const getFileKey = (file: File) =>
  `${file.name}-${file.size}-${file.lastModified}`;

export const useFileUploadState = ({
  setIsAddDataDrawerOpen,
  setDrawerFullSize,
  setDrawerTitle,
}: UseFileUploadStateArgs) => {
  const { selectedFiles, setSelectedFiles, setFileMetadata } =
    useCreateProjectState();

  const [previewFileIndex, setPreviewFileIndex] = React.useState<number | null>(
    null
  );

  const {
    fullFileStatsByFileIdx,
    fieldsByFileIdx,
    isParsing,
    isPartialByFileIdx,
    rowsByFileIdx,
  } = useProjectMultipleFileUploadsParser(
    selectedFiles,
    defaultProjectFileUploadOptions
  );

  const prevSelectedFiles = usePrevious(selectedFiles);

  // Sync file metadata when parsing completes
  React.useEffect(() => {
    // A file-list change reindexes selectedFiles and fileMetadata in the same
    // render, but the parser's maps and its isParsing flag lag one parse cycle
    // behind — syncing on this render would write the old layout's parse onto
    // the reindexed entries.
    if (
      isParsing ||
      (prevSelectedFiles && !isEqual(prevSelectedFiles, selectedFiles))
    ) {
      return;
    }

    setFileMetadata((prevMetadata) => {
      const metadata: Record<number, FileCreationMetadata> = {
        ...prevMetadata,
      };
      let hasChanges = false;

      Object.keys(fieldsByFileIdx).forEach((idx) => {
        const index = Number(idx);
        // Ignore stale parser entries for files that are no longer selected.
        if (index >= selectedFiles.length) {
          return;
        }
        // Adding or removing any file re-parses every file with the default
        // options. That parse only describes files whose saved options are
        // those defaults — a file the preview panel re-parsed with its own
        // options keeps everything that panel saved.
        const saved = prevMetadata[index];
        if (
          saved?.fileOptions &&
          !isEqual(saved.fileOptions, defaultProjectFileUploadOptions)
        ) {
          return;
        }
        // Labels don't come from the parse, so edited ones carry across it.
        const newFields = mergeEditedFieldLabels(
          fieldsByFileIdx[index] || [],
          saved?.fields
        );
        const csvPreviewRows = rowsByFileIdx[index] || [];

        if (
          prevMetadata[index]?.fields !== newFields ||
          prevMetadata[index]?.rowCount !== csvPreviewRows.length ||
          !prevMetadata[index]?.fileOptions ||
          prevMetadata[index]?.cachedFileStats !== fullFileStatsByFileIdx[index]
        ) {
          hasChanges = true;
          metadata[index] = {
            ...prevMetadata[index],
            fields: newFields,
            isPartial: isPartialByFileIdx[index],
            rowCount: csvPreviewRows.length,
            csvPreviewRows,
            fileOptions:
              prevMetadata[index]?.fileOptions ||
              defaultProjectFileUploadOptions,
            fileName:
              prevMetadata[index]?.fileName ||
              selectedFiles[index]?.name.replace(/\.csv$/i, '') ||
              '',
            // A re-parse clears the parser's stats before the new ones
            // stream in, and the preview resolves first. Only advance once
            // they exist — otherwise adding a file wipes the totals of every
            // other file too, dropping their cards to the capped preview
            // count until the stream finishes.
            cachedFileStats:
              fullFileStatsByFileIdx[index] ??
              prevMetadata[index]?.cachedFileStats,
          };
        }
      });

      // Drop metadata for files that are no longer selected (e.g. removed) so a
      // later file added at the same index can't inherit the stale data.
      Object.keys(metadata).forEach((idx) => {
        const index = Number(idx);
        if (index >= selectedFiles.length) {
          delete metadata[index];
          hasChanges = true;
        }
      });

      return hasChanges ? metadata : prevMetadata;
    });
  }, [
    fullFileStatsByFileIdx,
    fieldsByFileIdx,
    isParsing,
    rowsByFileIdx,
    setFileMetadata,
    selectedFiles,
    prevSelectedFiles,
    isPartialByFileIdx,
  ]);

  const handleDropAccepted = React.useCallback(
    (files: File[]) => {
      setSelectedFiles((prev) => {
        const newFiles = files.filter((newFile) => {
          return !prev.some(
            (existingFile) =>
              existingFile.name === newFile.name &&
              existingFile.size === newFile.size &&
              existingFile.lastModified === newFile.lastModified
          );
        });
        return [...prev, ...newFiles];
      });
    },
    [setSelectedFiles]
  );

  const mainDropzone = useDropzone({
    accept: FileExtension.CSV,
    multiple: true,
    noClick: false,
    onDropAccepted: handleDropAccepted,
  });

  const addDrawerDropzone = useDropzone({
    accept: FileExtension.CSV,
    multiple: true,
    noClick: false,
    onDropAccepted: (files: File[]) => {
      handleDropAccepted(files);
      requestAnimationFrame(() => {
        setIsAddDataDrawerOpen(false);
      });
    },
  });

  const handleRemoveFile = React.useCallback(
    (index: number) => {
      setSelectedFiles((prev) => prev.filter((_, i) => i !== index));

      setFileMetadata((prevMetadata) => {
        const reindexedMetadata: Record<number, FileCreationMetadata> = {};

        Object.entries(prevMetadata).forEach(([key, value]) => {
          const oldIndex = Number(key);
          if (oldIndex < index) {
            reindexedMetadata[oldIndex] = value;
          } else if (oldIndex > index) {
            reindexedMetadata[oldIndex - 1] = value;
          }
        });

        return reindexedMetadata;
      });
    },
    [setSelectedFiles, setFileMetadata]
  );

  const handleViewPreview = React.useCallback(
    (index: number) => {
      setPreviewFileIndex(index);
      setDrawerFullSize(true);
      setDrawerTitle(
        copy.components.projectResourceDialog.fileUploadPanel.title
      );
    },
    [setDrawerFullSize, setDrawerTitle]
  );

  const handleClosePreview = React.useCallback(() => {
    setPreviewFileIndex(null);
    setDrawerFullSize(false);
  }, [setDrawerFullSize]);

  const clearFiles = React.useCallback(() => {
    setSelectedFiles([]);
    setFileMetadata({});
  }, [setSelectedFiles, setFileMetadata]);

  return {
    previewFileIndex,
    mainDropzone,
    addDrawerDropzone,
    getFileKey,
    handleRemoveFile,
    handleViewPreview,
    handleClosePreview,
    clearFiles,
  };
};
